import LangToggle from "components/LangToggle.component";
import PageHeading from "components/PageHeading.component";
import { Card, ConfigProvider, Flex, Table, TableProps, InputRef } from "antd";
import { colors } from "shared/theme/color";

import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { Business } from "shared/types";
import { getAllBusiness } from "hooks/queries/business";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { useRef } from "react";
import { PAGE_SIZE } from "shared/constants";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import { translate } from "shared/languages";
import RegisterBusinessModal from "components/modals/RegisterBusinessModal.component";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import {
  StatusBadge,
  StatusTone,
} from "pages/schedule/component/ScheduleStatusBadge";
import { th, tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { capitalizeFirstLetter } from "shared/utils";
import { formatPhone } from "shared/utils/phone";

// Business-type chip tone (a label, no dot): Fitness navy, Beauty purple,
// Wellness green, anything else grey.
const businessTypeTone = (businessType: string): StatusTone => {
  switch (businessType.toLowerCase()) {
    case "fitness":
      return "navy";
    case "beauty":
      return "purple";
    case "wellness":
      return "done";
    default:
      return "neutral";
  }
};

const BusinessPage = () => {
  const navigate = useNavigate();
  const { data: businesses, isLoading } = getAllBusiness({});
  const { page, handleTableChange } = useRememberedTablePage("businesses");

  const searchInput = useRef<InputRef>(null);

  const columns: TableProps<Business>["columns"] = [
    {
      title: th(translate("label.name") || "Business Name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name > b.name ? 1 : -1),
      render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
      ...getColumnSearchProps({ config: "name", searchInput }),
    },
    {
      title: th(translate("label.email") || "Email"),
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps({ config: "email", searchInput }),
      render: (text) => (
        <Flex align="center" gap={8}>
          <MailOutlined style={{ color: colors.ink3 }} />
          <span>{text}</span>
        </Flex>
      ),
    },
    {
      title: th(translate("label.phone_number") || "Phone Number"),
      dataIndex: "phone_number",
      key: "phone_number",
      ...getColumnSearchProps({
        config: { dataIndex: "phone_number", searchType: "phone" },
        searchInput,
      }),
      render: (text) => (
        <Flex align="center" gap={8}>
          <PhoneOutlined style={{ color: colors.ink3 }} />
          <span style={tnum}>{formatPhone(text as string)}</span>
        </Flex>
      ),
    },
    {
      title: th(translate("label.business_type") || "Business Type"),
      dataIndex: "business_type",
      key: "business_type",
      sorter: (a, b) => (a.business_type > b.business_type ? 1 : -1),
      render: (businessType) => (
        <StatusBadge
          tone={businessTypeTone(businessType)}
          label={businessType ? capitalizeFirstLetter(businessType) : ""}
          dot={false}
        />
      ),
    },
    {
      title: th(translate("label.address") || "Address"),
      dataIndex: "address",
      key: "address",
      render: (text) => (
        <Flex align="center" gap={8}>
          <EnvironmentOutlined style={{ color: colors.ink3 }} />
          <span
            style={{
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </span>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title={translate("business") || "Businesses"}
        extra={<LangToggle />}
      />
      <Flex vertical gap={20}>
        <Card styles={{ body: { padding: 18 } }}>
          {/* Toolbar: register-business CTA (navy); the modal-buttons theme
              also gives its modal the ink Cancel. */}
          <Flex justify={"flex-end"} gap={10} style={{ marginBottom: 14 }}>
            <ConfigProvider theme={modalButtonsTheme}>
              <RegisterBusinessModal />
            </ConfigProvider>
          </Flex>
          <Table
            showSorterTooltip={false}
            sticky
            rowKey="id"
            size={"middle"}
            loading={isLoading}
            columns={columns}
            dataSource={businesses}
            onRow={(record) => {
              const business_id = record.id;
              return {
                onClick: () => navigate(`${PATHS.BUSINESS}/${business_id}`),
                style: { cursor: "pointer" },
              };
            }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
          />
        </Card>
      </Flex>
    </>
  );
};

export default BusinessPage;
